import React, { useMemo } from 'react';
import { Box } from '@interstate/components/Box';
import { InformationCircleIcon } from '@interstate/components/Icons/InformationCircleIcon';
import {
    SimpleTable,
    TableHead as InterstateTableHead,
    TableBody as InterstateTableBody,
    TableRow,
    TableCell
} from '@interstate/components/SimpleTable';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import { CheckBox } from '@interstate/components/CheckBox';
import { Typography } from '@interstate/components/Typography';
import { Tooltip } from '@interstate/components/Tooltip';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { FourSquareFieldKey } from './constants';
import { FOUR_SQUARE_FIELD_KEYS, FOUR_SQUARE_GENERAL_LABELS, FOUR_SQUARE_ALWAYS_ACTIVE_FIELDS } from './constants';

type FourSquareFieldConfig = Record<FourSquareFieldKey, { active: boolean; bold: boolean; displayName: string }>;

const ALWAYS_ACTIVE_TOOLTIP = 'Always included in Four Square. Required for the total amount calculation.';

interface FourSquareGeneralTabProps {
    fieldConfiguration: FourSquareFieldConfig;
    onActiveChange: (fieldKey: FourSquareFieldKey, checked: boolean) => void;
    disabled?: boolean;
}

const FourSquareGeneralTabBase: React.FC<FourSquareGeneralTabProps> = ({ fieldConfiguration, onActiveChange, disabled }) => {
    const theme = useInterstateTheme();
    const tableData = useMemo(
        () =>
            FOUR_SQUARE_FIELD_KEYS.filter((key) => key in fieldConfiguration).map((fieldKey) => ({
                fieldKey,
                label: FOUR_SQUARE_GENERAL_LABELS[fieldKey]
            })),
        [fieldConfiguration]
    );

    const alwaysActiveFields = useMemo(
        () =>
            Object.entries(FOUR_SQUARE_ALWAYS_ACTIVE_FIELDS).map(([fieldKey, label]) => ({
                fieldKey,
                label
            })),
        []
    );

    const activeCount = Object.values(fieldConfiguration).filter((config) => config.active).length + alwaysActiveFields.length;

    const handleActiveChange = (fieldKey: FourSquareFieldKey, e: InterstateOnChangeEvent<CheckBoxEventValue>) => {
        onActiveChange(fieldKey, e.target.value.checked);
    };

    return (
        <Box>
            {!disabled && (
                <Box display="flex" alignItems="center" gap="8px" mt={2} mb={1}>
                    <InformationCircleIcon type="info" />
                    <Typography variant="body-md">Check boxes to select the fields to print by default.</Typography>
                </Box>
            )}

            <SimpleTable data-testid="four-square-field-configuration-table" dataDensity="large" background="white" hover={false}>
                <InterstateTableHead>
                    <TableRow>
                        <TableCell>
                            <Box>Field</Box>
                        </TableCell>
                        <TableCell>
                            <Box display="flex" justifyContent="flex-end">
                                <Box sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{`Active (${activeCount})`}</Box>
                            </Box>
                        </TableCell>
                    </TableRow>
                </InterstateTableHead>
                <InterstateTableBody>
                    {tableData.length > 0 &&
                        alwaysActiveFields.map((item) => (
                            <TableRow key={item.fieldKey} data-field-key={item.fieldKey}>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap="8px">
                                        <span>{item.label}</span>
                                        {!disabled && (
                                            <Tooltip size="large" position="right" toolTipContent={ALWAYS_ACTIVE_TOOLTIP}>
                                                <Box
                                                    data-testid={`four-square-tooltip-${item.fieldKey}`}
                                                    display="flex"
                                                    alignItems="center"
                                                >
                                                    <InformationCircleIcon color={theme.tokens.BaseColorBlue500} />
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box display="flex" justifyContent="flex-end" mr={3}>
                                        <CheckBox
                                            value={`active-${item.fieldKey}`}
                                            checked={true}
                                            data-testid={`four-square-active-checkbox-${item.fieldKey}`}
                                            disabled={true}
                                        />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    {tableData.map((item) => {
                        const config = fieldConfiguration[item.fieldKey];
                        return (
                            <TableRow key={item.fieldKey} data-field-key={item.fieldKey}>
                                <TableCell>
                                    <Box>{item.label}</Box>
                                </TableCell>
                                <TableCell>
                                    <Box display="flex" justifyContent="flex-end" mr={3}>
                                        <CheckBox
                                            value={`active-${item.fieldKey}`}
                                            checked={config.active}
                                            onChange={(e) => handleActiveChange(item.fieldKey, e)}
                                            data-testid={`four-square-active-checkbox-${item.fieldKey}`}
                                            disabled={disabled}
                                        />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </InterstateTableBody>
            </SimpleTable>
        </Box>
    );
};

export const FourSquareGeneralTab = React.memo(FourSquareGeneralTabBase);
